import { useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';

import { useToggle } from '@/hooks/use-toggle';

import { useFetchAllRolesQuery } from '@/query/role.query';
import { useUsersQuery } from '@/query/user.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { getColumns } from './components/columns';
import { MemberEmptyResult } from './components/empty';
import { InviteUser } from './components/invite-user';

import { useMembersListStore } from '@/store/members-list.store';
import type { User } from '@/types/user.type';
import { type ColumnDef, type SortingState } from '@tanstack/react-table';

export const MemberList = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { state: isInviteUserOpen, toggle: toggleInviteUser } = useToggle();

  const { currentPage, pageSize, setCurrentPage, setPageSize } = useMembersListStore();

  const { data: usersData, isLoading } = useUsersQuery({
    page: currentPage,
    page_size: pageSize,
  });

  const { data } = useFetchAllRolesQuery();

  const members: User[] = usersData?.data || [];
  const meta = usersData?.meta;
  const roles = data || [];

  const columns = useMemo<ColumnDef<User>[]>(() => getColumns(roles), [roles]);

  return (
    <>
      <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between px-5 py-3">
          <CardTitle className="text-sm">Team members</CardTitle>
          <ProtectedComponent requiredPermissions={[Permissions.UsersInvite]}>
            <Button variant="outline" size={'sm'} onClick={toggleInviteUser}>
              <UserPlus className="h-4 w-4" />
              Invite members
            </Button>
          </ProtectedComponent>
        </CardHeader>
        <CardContent className="p-0 bg-card rounded-xl shadow-sm flex-1">
          <DataTable
            columns={columns}
            data={members}
            sorting={sorting}
            isLoading={isLoading}
            onSortingChange={setSorting}
            emptyState={<MemberEmptyResult />}
            headerClassName="bg-transparent"
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </CardContent>
      </Card>

      {/* Invite Members Modal */}
      {isInviteUserOpen && <InviteUser toggle={toggleInviteUser} roles={roles} />}
    </>
  );
};
